import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, RefreshCw, CheckCircle, XCircle, ArrowRight, Sparkles, Users, ClipboardCheck, UserPlus } from 'lucide-react';
import { PlanGeneration } from '../components/PlanGeneration';
import { PlanDisplay } from '../components/PlanDisplay';
import { OnboardingWizard } from '../components/onboarding-wizard';
import { api } from '../services/api';
import { aiIntelligenceService } from '../services/aiIntelligenceService';

type PageState = 'loading' | 'prerequisites' | 'onboarding-wizard' | 'plan-generation' | 'plan-display' | 'error';

interface Prerequisite {
  id: string;
  label: string;
  description: string;
  met: boolean;
  actionLabel: string;
  actionUrl: string;
  icon: React.ReactNode;
}

export function DevelopmentCyclePage() {
  const [teams, setTeams] = useState<{ id: string; name: string; ageGroup: string }[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>(undefined);
  const [playerCount, setPlayerCount] = useState<number>(0);
  const [assessedCount, setAssessedCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [activePlanId, setActivePlanId] = useState<string | null>(null);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch teams, players, and assessments to determine prerequisites
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch teams
      const teamsResponse = await api.get<{ data: { id: string; name: string; ageGroup: string }[] }>('/teams');
      const teamsData = teamsResponse.data.data || [];
      setTeams(teamsData);

      if (teamsData.length === 0) {
        setLoading(false);
        return;
      }

      const teamId = teamsData[0].id;
      setSelectedTeamId(teamId);

      // Fetch players for the first team
      const playersResponse = await api.get<{ data: { id: string }[] }>(`/teams/${teamId}/players`);
      const players = playersResponse.data.data || [];
      setPlayerCount(players.length);

      // Fetch assessments to check how many players have been assessed
      if (players.length > 0) {
        try {
          // Check each player for assessments (the API is per-player)
          let assessed = 0;
          for (const player of players) {
            try {
              const playerAssessments = await api.get<{ data: { id: string }[] }>(`/assessments/player/${player.id}`);
              const completedAssessments = playerAssessments.data.data || [];
              if (completedAssessments.length > 0) {
                assessed++;
              }
            } catch {
              // Individual player check failed — skip
            }
          }
          setAssessedCount(assessed);
        } catch {
          // Assessment service might not be available — treat as 0 assessed
          setAssessedCount(0);
        }
      }

      // Check onboarding status from AI service (non-blocking)
      try {
        const status = await aiIntelligenceService.getOnboardingStatus(teamId);
        setOnboardingComplete(status.onboardingComplete || false);
      } catch {
        // AI service not available yet — that's fine, onboarding not complete
        setOnboardingComplete(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load your team data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Determine page state based on data
  useEffect(() => {
    if (loading) {
      setPageState('loading');
      return;
    }

    if (error) {
      setPageState('error');
      return;
    }

    const hasTeam = teams.length > 0;
    const hasPlayers = playerCount >= 5;
    const allAssessed = hasPlayers && assessedCount >= playerCount;
    const allPrerequisitesMet = hasTeam && hasPlayers && allAssessed;

    if (!allPrerequisitesMet) {
      setPageState('prerequisites');
      return;
    }

    if (!onboardingComplete) {
      setPageState('onboarding-wizard');
      return;
    }

    setPageState('plan-generation');
  }, [loading, error, teams, playerCount, assessedCount, onboardingComplete]);

  const handleTeamChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTeamId(e.target.value);
    // Re-fetch data for the new team
    fetchData();
  };

  const handlePlanReady = useCallback((planId: string) => {
    setActivePlanId(planId);
    setPageState('plan-display');
  }, []);

  // Build prerequisites list
  const prerequisites: Prerequisite[] = [
    {
      id: 'team',
      label: 'Create a team',
      description: 'Set up your team profile with age group and format so the AI understands your coaching context.',
      met: teams.length > 0,
      actionLabel: 'Create Team',
      actionUrl: '/team/create',
      icon: <Users className="w-5 h-5" />,
    },
    {
      id: 'players',
      label: 'Add at least 5 players',
      description: 'Add your squad so the AI can build personalised development plans based on individual and team needs.',
      met: playerCount >= 5,
      actionLabel: 'Add Players',
      actionUrl: teams.length > 0 ? `/team/${teams[0]?.id}` : '/team/create',
      icon: <UserPlus className="w-5 h-5" />,
    },
    {
      id: 'assessments',
      label: 'Complete assessments for all players',
      description: 'Assess each player using the Skills Development Model. This data drives the AI\'s recommendations for training focus areas.',
      met: playerCount >= 5 && assessedCount >= playerCount,
      actionLabel: 'Assess Players',
      actionUrl: '/assess',
      icon: <ClipboardCheck className="w-5 h-5" />,
    },
  ];

  const metCount = prerequisites.filter(p => p.met).length;

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="p-4 lg:p-8 max-w-7xl mx-auto">
        <PageHeader />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-gray-500">Checking your setup...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (pageState === 'error') {
    return (
      <div className="p-4 lg:p-8 max-w-7xl mx-auto">
        <PageHeader />
        <div className="flex items-center justify-center py-20">
          <div className="text-center max-w-md">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load</h3>
            <p className="text-sm text-gray-600 mb-4">{error}</p>
            <button
              onClick={fetchData}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors min-h-[44px]"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Prerequisites state — show what's needed
  if (pageState === 'prerequisites') {
    return (
      <div className="p-4 lg:p-8 max-w-7xl mx-auto">
        <PageHeader />
        <div className="max-w-2xl mx-auto">
          {/* Intro */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              AI-Powered Development Planning
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed max-w-lg mx-auto">
              Generate personalised multi-week training blocks tailored to your coaching philosophy and your players' assessment data. Complete the steps below to unlock this feature.
            </p>
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Setup progress</span>
              <span>{metCount} of {prerequisites.length} complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="h-2.5 rounded-full bg-green-500 transition-all duration-500"
                style={{ width: `${(metCount / prerequisites.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Prerequisites list */}
          <div className="space-y-3 mb-8">
            {prerequisites.map((prereq) => (
              <div
                key={prereq.id}
                className={`rounded-xl border-2 p-4 transition-all ${
                  prereq.met
                    ? 'border-green-200 bg-green-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 mt-0.5 ${prereq.met ? 'text-green-600' : 'text-gray-400'}`}>
                    {prereq.met ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <XCircle className="w-5 h-5" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${prereq.met ? 'text-green-800' : 'text-gray-900'}`}>
                      {prereq.label}
                    </p>
                    {!prereq.met && (
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                        {prereq.description}
                      </p>
                    )}
                  </div>
                  {!prereq.met && (
                    <Link
                      to={prereq.actionUrl}
                      className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors min-h-[32px]"
                    >
                      {prereq.actionLabel}
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Why this matters */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Why these steps matter</h3>
            <p className="text-xs text-gray-600 leading-relaxed">
              The AI uses your team's real assessment data and your coaching philosophy to generate contextually relevant training plans. Without assessment data, the recommendations would be generic. With your players' actual strengths and weaknesses, the AI can prioritise the right development areas and create age-appropriate, progressive sessions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      {/* Page header with team selector */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <PageHeader inline />
        {teams.length > 1 && (
          <select
            value={selectedTeamId}
            onChange={handleTeamChange}
            className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 min-h-[44px]"
            aria-label="Select team"
          >
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name} ({team.ageGroup})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Onboarding Wizard */}
      {pageState === 'onboarding-wizard' && selectedTeamId && (
        <OnboardingWizard
          teamId={selectedTeamId}
          onComplete={() => {
            setOnboardingComplete(true);
            setPageState('plan-generation');
          }}
        />
      )}

      {/* Plan Generation */}
      {pageState === 'plan-generation' && selectedTeamId && (
        <PlanGeneration teamId={selectedTeamId} onPlanReady={handlePlanReady} />
      )}

      {/* Plan Display */}
      {pageState === 'plan-display' && activePlanId && (
        <PlanDisplay
          planId={activePlanId}
          onBack={() => {
            setActivePlanId(null);
            setPageState('plan-generation');
          }}
        />
      )}
    </div>
  );
}

function PageHeader({ inline }: { inline?: boolean }) {
  if (inline) {
    return (
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">Development Cycle</h1>
        <p className="text-gray-600">AI-powered development planning</p>
      </div>
    );
  }
  return (
    <div className="mb-6">
      <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">Development Cycle</h1>
      <p className="text-gray-600">AI-powered development planning</p>
    </div>
  );
}
